import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styles from './Tooltip.module.css';

// Hover-tooltip that renders into document.body via a portal. Needed because
// table action cells live inside an overflow:auto wrapper that clips any
// absolute-positioned tooltip extending past the table edge. A portaled
// position:fixed bubble escapes the wrapper entirely.
//
// `delayMs` controls the hover delay before the bubble appears (default 600ms).
// `multiline` is for a sentence or two of help: the bubble wraps at a fixed
// width and is kept inside the window, so it is not cut off beside a control
// at the page's edge.

const MULTILINE_WIDTH = 280; // Tooltip.module.css .multiline
const EDGE_GAP = 8;

const Tooltip = ({ text, children, delayMs = 600, multiline = false }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const compute = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const centre = r.left + r.width / 2;
    const half = MULTILINE_WIDTH / 2 + EDGE_GAP;
    setPos({
      top: r.top - 6, // gap above the element
      left: multiline
        ? Math.min(Math.max(centre, half), window.innerWidth - half)
        : centre,
    });
  }, [multiline]);

  const onEnter = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      compute();
      setShow(true);
    }, delayMs);
  };

  const onLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!text) return children;

  return (
    <span
      ref={wrapRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={styles.wrap}
    >
      {children}
      {show &&
        createPortal(
          <div
            className={`${styles.tooltip} ${multiline ? styles.multiline : ''}`}
            style={{ top: pos.top, left: pos.left }}
            role="tooltip"
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
};

Tooltip.propTypes = {
  text: PropTypes.string,
  children: PropTypes.node.isRequired,
  delayMs: PropTypes.number,
  multiline: PropTypes.bool,
};

export default Tooltip;
